/**
 * Warped text rendering utilities for the Studio canvas editor.
 * Draws text following curved paths using HTML5 Canvas 2D API.
 */

export type WarpType =
  | "none"
  | "arch-up"
  | "arch-down"
  | "circle-up"
  | "circle-down"
  | "wave"
  | "flag"
  | "bulge"
  | "perspective-left"
  | "perspective-right";

export interface WarpTextOptions {
  text: string;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  fontStyleStr: string;
  fill: string;
  letterSpacing: number;
  warpType: WarpType;
  warpAmount: number;
  strokeColor?: string;
  strokeWidth?: number;
  shadowBlur?: number;
  shadowColor?: string;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
}

function setupFont(ctx: CanvasRenderingContext2D, opts: WarpTextOptions): void {
  ctx.font = `${opts.fontStyleStr} ${opts.fontSize}px "${opts.fontFamily}"`;
  ctx.fillStyle = opts.fill;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  if (opts.shadowBlur || opts.shadowOffsetX || opts.shadowOffsetY) {
    ctx.shadowBlur = opts.shadowBlur || 0;
    ctx.shadowColor = opts.shadowColor || "rgba(0,0,0,0.5)";
    ctx.shadowOffsetX = opts.shadowOffsetX || 0;
    ctx.shadowOffsetY = opts.shadowOffsetY || 0;
  }
}

function getCharWidths(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  letterSpacing: number
): number[] {
  return chars.map((c) => ctx.measureText(c).width + letterSpacing);
}

function drawChar(
  ctx: CanvasRenderingContext2D,
  char: string,
  opts: WarpTextOptions
): void {
  ctx.fillText(char, 0, 0);
  if (opts.strokeWidth && opts.strokeColor) {
    ctx.strokeStyle = opts.strokeColor;
    ctx.lineWidth = opts.strokeWidth;
    ctx.strokeText(char, 0, 0);
  }
}

function drawArchText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions,
  direction: "up" | "down"
): void {
  const amount = Math.max(1, opts.warpAmount) / 100;
  const arcAngle = Math.max(0.05, amount) * Math.PI * 1.4;
  const radius = Math.max(10, totalWidth / arcAngle);
  const cx = opts.width / 2;
  let angle = -arcAngle / 2;

  // Center the arch vertically inside the bounding box.
  // For arch-up: top of arc is at cy-radius, bottom at cy-radius*cos(arcAngle/2).
  // Setting cy = height/2 + radius*(1+cos(arcAngle/2))/2 centers that span around height/2.
  // For arch-down: mirror the same logic.
  const halfSpan = radius * (1 + Math.cos(arcAngle / 2)) / 2;

  if (direction === "up") {
    const cy = opts.height / 2 + halfSpan;
    chars.forEach((char, i) => {
      const charAngle = charWidths[i] / radius;
      const midAngle = angle + charAngle / 2;
      const px = cx + radius * Math.sin(midAngle);
      const py = cy - radius * Math.cos(midAngle);
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(midAngle);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      drawChar(ctx, char, opts);
      ctx.restore();
      angle += charAngle;
    });
  } else {
    const cy = opts.height / 2 - halfSpan;
    chars.forEach((char, i) => {
      const charAngle = charWidths[i] / radius;
      const midAngle = angle + charAngle / 2;
      const px = cx + radius * Math.sin(midAngle);
      const py = cy + radius * Math.cos(midAngle);
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-midAngle);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      drawChar(ctx, char, opts);
      ctx.restore();
      angle += charAngle;
    });
  }
}

function drawCircleText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions,
  onTop: boolean
): void {
  const radius = Math.min(opts.width, opts.height) * 0.38;
  const cx = opts.width / 2;
  const cy = opts.height / 2;
  const arcAngle = totalWidth / radius;
  let angle = -arcAngle / 2 - Math.PI / 2;

  chars.forEach((char, i) => {
    const charAngle = charWidths[i] / radius;
    const midAngle = angle + charAngle / 2;
    const px = cx + radius * Math.cos(midAngle);
    const py = cy + radius * Math.sin(midAngle);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(onTop ? midAngle + Math.PI / 2 : midAngle - Math.PI / 2);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawChar(ctx, char, opts);
    ctx.restore();
    angle += charAngle;
  });
}

function drawWaveText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions
): void {
  const amount = opts.warpAmount / 100;
  const amplitude = amount * opts.fontSize * 0.6;
  const cy = opts.height / 2;
  let currentX = (opts.width - totalWidth) / 2;

  chars.forEach((char, i) => {
    const progress =
      (currentX + charWidths[i] / 2 - (opts.width - totalWidth) / 2) /
      Math.max(1, totalWidth);
    const waveY = cy + amplitude * Math.sin(progress * 2 * Math.PI);
    const waveRot = Math.atan(
      (amplitude * 2 * Math.PI * Math.cos(progress * 2 * Math.PI)) /
        Math.max(1, totalWidth)
    );
    ctx.save();
    ctx.translate(currentX + charWidths[i] / 2, waveY);
    ctx.rotate(waveRot);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawChar(ctx, char, opts);
    ctx.restore();
    currentX += charWidths[i];
  });
}

function drawFlagText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions
): void {
  const amount = opts.warpAmount / 100;
  const amplitude = amount * opts.fontSize * 0.8;
  const cy = opts.height / 2;
  let currentX = (opts.width - totalWidth) / 2;

  chars.forEach((char, i) => {
    const progress =
      (currentX - (opts.width - totalWidth) / 2) / Math.max(1, totalWidth);
    const waveY = cy + amplitude * Math.sin(progress * 2 * Math.PI + Math.PI / 4);
    const scale = 0.85 + 0.15 * Math.cos(progress * 2 * Math.PI);
    ctx.save();
    ctx.translate(currentX + charWidths[i] / 2, waveY);
    ctx.scale(scale, scale);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawChar(ctx, char, opts);
    ctx.restore();
    currentX += charWidths[i];
  });
}

function drawBulgeText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions
): void {
  const amount = opts.warpAmount / 100;
  const cy = opts.height / 2;
  let currentX = (opts.width - totalWidth) / 2;

  chars.forEach((char, i) => {
    const progress =
      (currentX + charWidths[i] / 2 - (opts.width - totalWidth) / 2) /
      Math.max(1, totalWidth);
    const t = 4 * progress * (1 - progress);
    const scale = 1 + amount * 0.6 * t;
    const yOffset = amount * opts.fontSize * 0.3 * t;
    ctx.save();
    ctx.translate(currentX + charWidths[i] / 2, cy - yOffset);
    ctx.scale(scale, scale);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawChar(ctx, char, opts);
    ctx.restore();
    currentX += charWidths[i];
  });
}

function drawPerspectiveText(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  charWidths: number[],
  totalWidth: number,
  opts: WarpTextOptions,
  side: "left" | "right"
): void {
  const amount = opts.warpAmount / 100;
  const cy = opts.height / 2;
  let currentX = (opts.width - totalWidth) / 2;

  chars.forEach((char, i) => {
    const progress =
      (currentX + charWidths[i] / 2 - (opts.width - totalWidth) / 2) /
      Math.max(1, totalWidth);
    const scale =
      side === "left"
        ? 0.5 + 0.5 * progress + amount * 0.4 * progress
        : 1.0 - progress * 0.5 * amount;
    const yOffset =
      side === "left"
        ? amount * opts.fontSize * 0.3 * (1 - progress)
        : amount * opts.fontSize * 0.3 * progress;
    ctx.save();
    ctx.translate(currentX + charWidths[i] * scale * 0.5, cy - yOffset);
    ctx.scale(scale, scale);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawChar(ctx, char, opts);
    ctx.restore();
    currentX += charWidths[i] * scale;
  });
}

/**
 * Draw warped text onto a 2D canvas context.
 * The context origin (0,0) is the top-left of the element bounding box.
 */
export function drawWarpedText(
  ctx: CanvasRenderingContext2D,
  opts: WarpTextOptions
): void {
  if (opts.warpType === "none" || !opts.text) return;
  ctx.save();
  setupFont(ctx, opts);
  const chars = [...opts.text];
  const charWidths = getCharWidths(ctx, chars, opts.letterSpacing);
  const totalWidth = charWidths.reduce((a, b) => a + b, 0);

  switch (opts.warpType) {
    case "arch-up":
      drawArchText(ctx, chars, charWidths, totalWidth, opts, "up");
      break;
    case "arch-down":
      drawArchText(ctx, chars, charWidths, totalWidth, opts, "down");
      break;
    case "circle-up":
      drawCircleText(ctx, chars, charWidths, totalWidth, opts, true);
      break;
    case "circle-down":
      drawCircleText(ctx, chars, charWidths, totalWidth, opts, false);
      break;
    case "wave":
      drawWaveText(ctx, chars, charWidths, totalWidth, opts);
      break;
    case "flag":
      drawFlagText(ctx, chars, charWidths, totalWidth, opts);
      break;
    case "bulge":
      drawBulgeText(ctx, chars, charWidths, totalWidth, opts);
      break;
    case "perspective-left":
      drawPerspectiveText(ctx, chars, charWidths, totalWidth, opts, "left");
      break;
    case "perspective-right":
      drawPerspectiveText(ctx, chars, charWidths, totalWidth, opts, "right");
      break;
  }
  ctx.restore();
}
